'use client'

import { api } from '@/features/veluvit/services/api'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

/* 스튜디오 이미지 업로드 — 서버는 이미지 칸에 500자까지만 받는다(renew_schema.py). 편집 중에는 고른 그림을 data URL 로 들고 있다가
   저장할 때(sync.ts) /creator/me/media/ 에 올리고 받은 주소(/api/media/<id>/)로 바꿔 보낸다.
   주소만으로는 영상인지 알 수 없어(서버가 주소에 확장자를 붙이지 않는다) 올린 · 불러온 파일의 종류를 여기 기억해 둔다. */

export type MediaAsset = { url: string; kind: string }

const videos = new Set<string>()
/** 같은 그림을 두 번 올리지 않게 — 동시에 두 엔티티가 같은 data URL 을 저장해도 요청은 하나 */
const uploads = new Map<string, Promise<string>>()
const EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'video/mp4': 'mp4',
}

export const isDataUrl = (value?: string | null): value is string =>
  !!value && value.startsWith('data:')

/** 서버에 올린 움직이는 표지(MP4)인가 — ui.tsx 의 isVideoSrc 가 data URL 과 함께 본다 */
export const isUploadedVideo = (src: string) => videos.has(src)

export function rememberAssets(assets: MediaAsset[]) {
  for (const asset of assets) if (asset.kind === 'video') videos.add(asset.url)
}

function toBlob(src: string) {
  const comma = src.indexOf(',')
  const mime = /^data:([^;,]+)/.exec(src)?.[1] ?? 'application/octet-stream'
  const binary = atob(src.slice(comma + 1))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

/** data URL 하나를 올리고 서버 주소를 돌려준다. 실패하면 기억을 지워 다음 저장 때 다시 올릴 수 있게 한다(자동 재전송은 없다) */
export function uploadDataUrl(
  src: string,
  ownerKey = '',
  purpose: 'cover' | 'titleImage' = 'cover'
): Promise<string> {
  const key = `${ownerKey}:${useSessionStore.getState().identity}:${purpose}:${src}`
  const known = uploads.get(key)
  if (known) return known
  const job = (async () => {
    const blob = toBlob(src)
    const form = new FormData()
    form.append('file', blob, `studio.${EXT[blob.type] ?? 'bin'}`)
    // 움직이는 그림은 작품 표지에만 들어간다(5초 이하) — 서버도 purpose=cover 로 5초를 검사한다
    form.append('purpose', purpose)
    if (blob.type.startsWith('video/')) form.append('kind', 'video')
    const asset = await api<MediaAsset>('/creator/me/media/', form)
    rememberAssets([asset])
    return asset.url
  })()
  uploads.set(key, job)
  void job.then(
    () => uploads.delete(key),
    () => uploads.delete(key)
  )
  return job
}
